// Option-set table columns — name tile (kind icon) + title, the Form/Question
// kind chip, the monospace SQL column, the option-value chips capped at three
// with a +N overflow (the people-table idiom), the absolute last-updated
// timestamp, the last-updated-by avatar, and the row-actions kebab. The kebab's
// menu lives in OptionSetRowActions — it needs its own open state — and its two
// callbacks are verb-gated by the tab: absent = hidden, and with neither the
// kebab renders nothing.
//
// The four `sortable` columns are exactly the confirmed OptionSetSortKey set,
// and each one's `key` IS the wire orderBy.key (which is why the Kind column is
// keyed 'type', not 'kind'). DataTable draws sort glyphs from `col.sortable`
// alone, so these flags only exist because the tab also passes `onSort`.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon } from '@/components/common';
import type { DataTableColumn } from '@/components/common';
import type { OptionSet, OptionSetKind, OptionSetPerson } from '@/api/endpoints/formSetsApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import OptionSetRowActions from './OptionSetRowActions';
import { fmtDateTime } from '@/utils/formatDate';

/** Kind → its icon, shared by the name tile and the kind chip (and the create
 *  modal's kind cards). Verified against the installed lucide. */
export const KIND_ICONS: Record<OptionSetKind, string> = {
  form_set: 'clipboard-list',
  question_set: 'circle-question-mark',
};

// Options column: chips shown before the count collapses into "+N".
const MAX_OPTION_CHIPS = 3;

const PersonCell = ({ person }: { person: OptionSetPerson | null }) => {
  if (!person) return <span className='option-set-table__muted'>—</span>;
  return (
    <span className='option-set-table__person'>
      <Avatar size={28} tone={toneOf(person.fullName)} initials={initialsOf(person.fullName)} />
      {person.fullName}
    </span>
  );
};

interface UseOptionSetColumnsArgs {
  /** Row kebab — verb-gated by the tab: absent = hidden in the kebab (the
   *  kebab itself renders nothing when both are absent). */
  onEdit?: (set: OptionSet) => void;
  onDelete?: (set: OptionSet) => void;
}

export const useOptionSetColumns = ({
  onEdit,
  onDelete,
}: UseOptionSetColumnsArgs = {}): DataTableColumn<OptionSet>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'title',
      label: t('data_structure_os_col_name'),
      width: 'minmax(12rem, 1.3fr)',
      sortable: true,
      render: (s) => (
        <span className='option-set-table__name'>
          <span className='option-set-table__tile' aria-hidden='true'>
            <Icon name={KIND_ICONS[s.kind]} size={18} />
          </span>
          <span className='option-set-table__title'>{s.title}</span>
        </span>
      ),
    },
    {
      key: 'type',
      label: t('data_structure_os_col_kind'),
      sortable: true,
      render: (s) => (
        <Chip iconStart={KIND_ICONS[s.kind]}>{t(`data_structure_os_kind_${s.kind}`)}</Chip>
      ),
    },
    {
      key: 'sqlColumn',
      label: t('data_structure_os_col_sql'),
      sortable: true,
      // SQL identifiers are always latin/lowercase — keep them LTR in Arabic.
      render: (s) => (
        <code className='option-set-table__sql' dir='ltr'>
          {s.sqlColumn}
        </code>
      ),
    },
    {
      key: 'options',
      label: t('data_structure_os_col_options'),
      // No `width`: an `auto` track floors at its widest cell and never
      // compresses, so a long chip row widens the column (table scrolls)
      // instead of spilling over Last updated. fr tracks compress to their
      // floor and would need a truncation chain these chips cannot have.
      render: (s) => {
        const shown = s.options.slice(0, MAX_OPTION_CHIPS);
        const extra = s.options.length - shown.length;
        if (s.options.length === 0) return <span className='option-set-table__muted'>—</span>;
        return (
          <span className='option-set-table__chips'>
            {shown.map((o) => (
              <Chip key={o.id}>{o.value}</Chip>
            ))}
            {extra > 0 && <span className='option-set-table__more'>+{extra}</span>}
          </span>
        );
      },
    },
    {
      key: 'updatedAt',
      label: t('data_structure_os_col_updated'),
      sortable: true,
      render: (s) => fmtDateTime(s.updatedAt),
    },
    {
      key: 'lastUpdatedBy',
      label: t('data_structure_os_col_updated_by'),
      width: 'minmax(11rem, 1fr)',
      render: (s) => <PersonCell person={s.lastUpdatedBy} />,
    },
    {
      key: 'actions',
      label: '',
      width: '3rem',
      align: 'end',
      render: (s) => <OptionSetRowActions set={s} onEdit={onEdit} onDelete={onDelete} />,
    },
  ];
};

export default useOptionSetColumns;
